import { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);

export const itemKey = (productId, variant) => (variant ? `${productId}::${variant.id}` : productId);
export const itemPrice = (item) => (item.variant && item.variant.price != null ? item.variant.price : item.product.price);
export const itemImage = (item) => item.variant?.image || item.variant?.images?.[0] || item.product.image;
export const itemLabel = (item) => (item.variant ? `${item.product.name} — ${item.variant.name}` : item.product.name);

export const CartProvider = ({ children }) => {
    const [items, setItems] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem("yt_cart") || "[]");
        } catch {
            return [];
        }
    });
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        localStorage.setItem("yt_cart", JSON.stringify(items));
    }, [items]);

    const addItem = (product, quantity = 1, variant = null) => {
        const key = itemKey(product.id, variant);
        setItems((prev) => {
            const existing = prev.find((i) => itemKey(i.product.id, i.variant) === key);
            if (existing) {
                return prev.map((i) =>
                    itemKey(i.product.id, i.variant) === key ? { ...i, quantity: Math.min(i.quantity + quantity, 99) } : i
                );
            }
            return [...prev, { product, variant, quantity }];
        });
        setIsOpen(true);
    };

    const removeItem = (key) => setItems((prev) => prev.filter((i) => itemKey(i.product.id, i.variant) !== key));

    const setQty = (key, quantity) => {
        if (quantity < 1) return removeItem(key);
        setItems((prev) => prev.map((i) => (itemKey(i.product.id, i.variant) === key ? { ...i, quantity: Math.min(quantity, 99) } : i)));
    };

    const clear = () => setItems([]);

    const value = useMemo(() => {
        const count = items.reduce((s, i) => s + i.quantity, 0);
        const total = items.reduce((s, i) => s + itemPrice(i) * i.quantity, 0);
        return { items, addItem, removeItem, setQty, clear, count, total, isOpen, openCart: () => setIsOpen(true), closeCart: () => setIsOpen(false) };
    }, [items, isOpen]);

    return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => useContext(CartContext);
